import { Add, List } from '@mui/icons-material';
import { Box, Button, Divider, Link } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import { SolaForceHeading } from '~/components/SolaForceHeading';
import { AbsenceType } from '~/data/absences/types';
import {
  AbsenceDecision,
  AbsenceDecisionDialog,
} from '~/features/AbsenceChanges/AbsenceDecisionDialog';
import { DeleteAbsenceDialog } from '~/features/AbsenceChanges/DeleteAbsenceDialog/component';
import { EditAbsenceDialog } from '~/features/AbsenceChanges/EditAbsenceDialog';
import { AbsencesTabContent_OnUpdateLegacy } from '~/library/AbsencesTabContent/component';
import { AbsenceActionsCell } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceActionsCell';
import { AbsenceHistoryDialog } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceHistoryDialog';
import { AbsenceStatusCell } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceStatusCell';
import { AbsenceStatusDecisionActions } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceStatusCell/AbsenceStatusDecisionActions';
import { useAbsenceTable } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/useAbsenceTable';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

export type AbsencesTableProps = {
  employeeId: number;
  absenceType: AbsenceType;
} & AbsencesTabContent_OnUpdateLegacy;

export const AbsencesTable = (props: AbsencesTableProps) => {
  const { absenceType, employeeId, onUpdateLegacy } = props;

  const { t } = useTranslation();

  const {
    isLoading,
    title,
    instructionDialogConfig,
    editDialog,
    deleteDialog,
    decisionDialog,
    historyDialogState,
    permissions: { canAddAbsence, canEditSelectedAbsence },
    table: {
      rows,
      columnConfigs,
      tableUtils: { filterAvailableColumns, getColumnsWithCellRendering },
    },
  } = useAbsenceTable(props);

  const { columnsConfigWidthRenders } = useMemo(() => {
    const c = getColumnsWithCellRendering({
      columnConfigs: filterAvailableColumns(columnConfigs),
      renderStatusComponent: (componentProps) => (
        <AbsenceStatusCell
          {...componentProps}
          decisionActions={(absenceId) => {
            const openDecision =
              (decision: AbsenceDecision) => (absenceId: string) => {
                decisionDialog.onOpen(absenceId, decision);
              };

            return (
              <AbsenceStatusDecisionActions
                absenceId={absenceId}
                onApprove={openDecision(AbsenceDecision.Approve)}
                onReject={openDecision(AbsenceDecision.Reject)}
                onRequestChange={openDecision(AbsenceDecision.RequestChanges)}
              />
            );
          }}
        />
      ),
      renderAttachmentComponent: (componentProps) => (
        <Link {...componentProps} />
      ),
      renderActionsComponent: ({ row }) => {
        const openDecision =
          (decision: AbsenceDecision) => (absenceId: string) => {
            decisionDialog.onOpen(absenceId, decision);
          };

        return (
          <AbsenceActionsCell
            absenceId={row.id}
            absenceEntry={row.meta}
            onEditAbsence={editDialog.onOpen}
            onDeleteAbsence={deleteDialog.onOpen}
            onApprove={openDecision(AbsenceDecision.Approve)}
            onReject={openDecision(AbsenceDecision.Reject)}
            onRequestChange={openDecision(AbsenceDecision.RequestChanges)}
            canApproveAbsence={false}
            disabled={!canEditSelectedAbsence(row.id)}
          />
        );
      },
    });

    return { columnsConfigWidthRenders: c };
  }, [
    filterAvailableColumns,
    getColumnsWithCellRendering,
    canEditSelectedAbsence,
    columnConfigs,
    editDialog,
    deleteDialog,
    decisionDialog,
  ]);

  const { isVerticalScrollEnabled, dataGridContainerSx } = useMemo(() => {
    const settings = {
      scrollRowCount: 20,
      rowHeight: 36,
      headerRowHeight: 39,
    };

    const isVerticalScrollEnabled = rows.length > settings.scrollRowCount;
    const dataGridHeight =
      settings.scrollRowCount * settings.rowHeight + settings.headerRowHeight;

    return {
      isVerticalScrollEnabled,
      dataGridContainerSx: isVerticalScrollEnabled
        ? { height: `${dataGridHeight}px` }
        : undefined,
    };
  }, [rows]);

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <Box>
      <SolaForceHeading heading={title} instruction={instructionDialogConfig}>
        {canAddAbsence && (
          <>
            <Button
              onClick={() => editDialog.onOpen()}
              startIcon={<Add />}
              data-testid="absTable-add-absence-button"
            >
              {t('laAdd')}
            </Button>
            <Divider orientation={'vertical'} variant={'middle'} flexItem />
            <Button
              onClick={() => historyDialogState.open()}
              startIcon={<List />}
            >
              {t('laHistory')}
            </Button>
          </>
        )}
      </SolaForceHeading>

      {rows.length > 0 && (
        <Box sx={dataGridContainerSx}>
          <SolaForceDataGrid
            columns={columnsConfigWidthRenders}
            rows={rows}
            autoHeight={!isVerticalScrollEnabled}
            initialState={{
              pinnedColumns: {
                right: ['status', 'actions'],
              },
              density: 'compact',
            }}
            hideFooter={true}
          />
        </Box>
      )}
      <EditAbsenceDialog
        employeeId={employeeId}
        absenceType={absenceType}
        onClose={editDialog.onClose}
        {...editDialog.state}
        onUpdateLegacy={onUpdateLegacy}
      />
      <DeleteAbsenceDialog
        employeeId={employeeId}
        onClose={deleteDialog.onClose}
        open={!!deleteDialog.state}
        absenceId={deleteDialog.state}
        onUpdateLegacy={onUpdateLegacy}
      />
      <AbsenceDecisionDialog
        employeeId={employeeId}
        {...decisionDialog.state}
        onClose={decisionDialog.onClose}
        onUpdateLegacy={onUpdateLegacy}
      />
      <AbsenceHistoryDialog
        employeeId={employeeId}
        absenceType={absenceType}
        open={historyDialogState.isOpen}
        onClose={historyDialogState.close}
      />
    </Box>
  );
};
